import Vue from 'vue';
import firebase, { database } from '@/plugins/firebase';
import { getSeasonYear } from '@/store/seasonYear';
import { callFn } from '@/utils/functionsClient';
import { coversDate } from '@/utils/contractPeriod';
export const DocumentTemplatesCollection = 'documentTemplates';
export const DocumentsCollection = 'documents';
function upsert(list, item) {
    const index = list.findIndex(x => x.id === item.id);
    if (index === -1) {
        list.push(item);
        return;
    }
    Vue.set(list, index, item);
}
function remove(list, id) {
    const index = list.findIndex(x => x.id === id);
    if (index !== -1)
        list.splice(index, 1);
}
export const DocumentModule = {
    namespaced: true,
    state: {
        templates: [],
        documents: [],
    },
    mutations: {
        storeTemplate(state, template) {
            upsert(state.templates, template);
        },
        removeTemplate(state, id) {
            remove(state.templates, id);
        },
        storeDocument(state, document) {
            upsert(state.documents, document);
        },
        removeDocument(state, id) {
            remove(state.documents, id);
        },
        clear(state) {
            state.templates.splice(0);
            state.documents.splice(0);
        },
    },
    actions: {
        async storeTemplate(context, data) {
            const { id } = await callFn('storeDocumentTemplate', data);
            return id;
        },
        async publishTemplate(context, data) {
            await callFn('publishDocumentTemplate', data);
        },
        async destroyTemplate(context, template) {
            await database.collection(DocumentTemplatesCollection).doc(template.id).update({
                deletedAt: firebase.firestore.Timestamp.now(),
            });
        },
        async sign(context, data) {
            const { id } = await callFn('signDocument', data);
            // The onSnapshot listener lags behind the callable; store the document now so the menu updates at once.
            const doc = await database.collection(DocumentsCollection).doc(id).get();
            context.commit('storeDocument', { id: doc.id, data: doc.data() });
        },
        async decline(context, data) {
            const { id } = await callFn('declineDocument', data);
            const doc = await database.collection(DocumentsCollection).doc(id).get();
            context.commit('storeDocument', { id: doc.id, data: doc.data() });
        },
        async countersign(context, data) {
            await callFn('countersignDocument', data);
        },
        async invalidate(context, documentId) {
            await callFn('invalidateDocument', { documentId });
        },
        async pdfUrl(context, document) {
            if (!document.data.pdfPath)
                throw new Error('PDF není k dispozici');
            return firebase.storage().ref(document.data.pdfPath).getDownloadURL();
        },
        async templatePdfUrl(context, template) {
            return firebase.storage().ref(template.data.pdfPath).getDownloadURL();
        },
        async listen(context, data) {
            context.commit('clear');
            const templatesUnsub = database.collection(DocumentTemplatesCollection).onSnapshot(snapshot => {
                for (const change of snapshot.docChanges()) {
                    if (change.type === 'removed') {
                        context.commit('removeTemplate', change.doc.id);
                    }
                    else {
                        context.commit('storeTemplate', { id: change.doc.id, data: change.doc.data() });
                    }
                }
            });
            let query = database.collection(DocumentsCollection);
            if (!data.isModerator) {
                query = query.where('instructorId', '==', data.userId);
            }
            const documentsUnsub = query.onSnapshot(snapshot => {
                for (const change of snapshot.docChanges()) {
                    if (change.type === 'removed') {
                        context.commit('removeDocument', change.doc.id);
                    }
                    else {
                        context.commit('storeDocument', { id: change.doc.id, data: change.doc.data() });
                    }
                }
            });
            return [templatesUnsub, documentsUnsub];
        },
    },
    getters: {
        templates(state) {
            return state.templates
                .filter(t => !t.data.deletedAt)
                .sort((a, b) => b.data.version - a.data.version);
        },
        /** The published template of that key covering the day. */
        templateFor(state) {
            return (key, date) => state.templates.find(t => t.data.key === key
                && t.data.published
                && !t.data.deletedAt
                && coversDate(t.data, date));
        },
        documents(state) {
            return state.documents;
        },
        /** Signed, declined or awaiting the school - any decision on that key for the period covering the day. */
        decisionFor(state) {
            return (uid, key, date) => state.documents.find(d => {
                if (d.data.instructorId !== uid)
                    return false;
                if (d.data.key !== key)
                    return false;
                if (d.data.season !== getSeasonYear())
                    return false;
                if (d.data.status === 'invalidated')
                    return false;
                const template = state.templates.find(t => t.id === d.data.templateId);
                return !!template && coversDate(template.data, date);
            });
        },
    },
};
